import { QueryObserverResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createBoardSetting,
  deleteBoardSetting,
  editBoardSetting,
  getBoardSettings,
  mapSubprojectsToBoardSetting,
  removeSubprojectsFromBoardSetting,
} from './board-settings-client';
import type { BoardSetting, CreateBoardSettingPayload, EditBoardSettingPayload } from './board-settings-client.type';

/**
 * Hook to fetch board settings for an organization
 *
 * @param {string | null} params.organization_id - ID of the organization to fetch board settings for
 * @returns {Object} Object containing:
 *   - data: The board settings data
 *   - query: The React Query result object
 */
const useBoardSettings = ({
  organization_id,
}: {
  organization_id: string | null;
}): { data: BoardSetting[] | undefined; query: QueryObserverResult<BoardSetting[]> } => {
  const query = useQuery({
    queryKey: ['board-settings', organization_id],
    queryFn: () => getBoardSettings(organization_id),
    enabled: !!organization_id,
  });

  return { data: query.data, query };
};

/**
 * Hook for creating new board settings
 *
 * @returns Object containing:
 * - useCreateBoardSettingHook: Function to create a new board setting and return it
 * - isCreating: Boolean indicating if creation is in progress
 */

const useCreateBoardSetting = (): {
  useCreateBoardSettingHook: (payload: CreateBoardSettingPayload) => Promise<BoardSetting>;
  isCreating: boolean;
} => {
  const create = useMutation({
    mutationFn: createBoardSetting,
    // Note: No automatic cache invalidation here since we manually refetch in the calling code
    // to ensure data is fresh before transitioning states
  });

  const useCreateBoardSettingHook = async (payload: CreateBoardSettingPayload): Promise<BoardSetting> => {
    const result = await create.mutateAsync(payload);
    return result;
  };

  return {
    useCreateBoardSettingHook,
    isCreating: create.isPending,
  };
};

/**
 * Hook for deleting a board setting
 *
 * @returns Object containing:
 * - deleteBoardSetting: Function to delete a board setting and return it
 * - isDeleting: Boolean indicating if deletion is in progress
 */
const useDeleteBoardSetting = (): {
  deleteBoardSetting: (id: string) => Promise<void>;
  isDeleting: boolean;
} => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: deleteBoardSetting,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board-settings'] });
      queryClient.invalidateQueries({ queryKey: ['integration-entries'] });
    },
  });

  return {
    deleteBoardSetting: (id: string) => mutation.mutateAsync(id),
    isDeleting: mutation.isPending,
  };
};

const useEditBoardSetting = () => {
  const mutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: EditBoardSettingPayload }) => editBoardSetting(id, payload),
  });

  return {
    editBoardSetting: (id: string, payload: EditBoardSettingPayload) => mutation.mutateAsync({ id, payload }),
    isEditing: mutation.isPending,
  };
};

/**
 * Hook for adding subprojects to a board setting
 *
 * @returns Object containing:
 * - addSubprojectsToBoardSetting: Function to add subprojects to a board setting
 * - isAdding: Boolean indicating if addition is in progress
 */
const useAddSubprojectToBoardSetting = (): {
  addSubprojectsToBoardSetting: (
    boardSettingId: string,
    subprojectIds: string[],
    organizationId: string,
  ) => Promise<void>;
  isAdding: boolean;
} => {
  const mutation = useMutation({
    mutationFn: ({
      boardSettingId,
      subprojectIds,
      organizationId,
    }: {
      boardSettingId: string;
      subprojectIds: string[];
      organizationId: string;
    }) => mapSubprojectsToBoardSetting(boardSettingId, subprojectIds, organizationId),
  });

  return {
    addSubprojectsToBoardSetting: (boardSettingId: string, subprojectIds: string[], organizationId: string) =>
      mutation.mutateAsync({ boardSettingId, subprojectIds, organizationId }),
    isAdding: mutation.isPending,
  };
};

/**
 * Hook for removing subprojects from a board setting
 *
 * @returns Object containing:
 * - removeSubprojectsFromBoardSetting: Function to remove subprojects from a board setting
 * - isRemoving: Boolean indicating if removal is in progress
 */
const useRemoveSubprojectFromBoardSetting = (): {
  removeSubprojectsFromBoardSetting: (
    boardSettingId: string,
    subprojectIds: string[],
    organizationId: string,
  ) => Promise<void>;
  isRemoving: boolean;
} => {
  const mutation = useMutation({
    mutationFn: ({
      boardSettingId,
      subprojectIds,
      organizationId,
    }: {
      boardSettingId: string;
      subprojectIds: string[];
      organizationId: string;
    }) => removeSubprojectsFromBoardSetting(boardSettingId, subprojectIds, organizationId),
  });

  return {
    removeSubprojectsFromBoardSetting: (boardSettingId: string, subprojectIds: string[], organizationId: string) =>
      mutation.mutateAsync({ boardSettingId, subprojectIds, organizationId }),
    isRemoving: mutation.isPending,
  };
};

export {
  useAddSubprojectToBoardSetting,
  useBoardSettings,
  useCreateBoardSetting,
  useDeleteBoardSetting,
  useEditBoardSetting,
  useRemoveSubprojectFromBoardSetting,
};
